import type {Moment} from 'moment';

import {resource} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';

// The Clever user query is built by untyped legacy utils; this is the narrow shape
// the dashboard surfaces (home card, Create-New menu) read off the resource.
export interface CleverUser {
  getCleverId: () => string | undefined;
  getDistrictCleverId: () => string | undefined;
  getMeta: () => {
    isAllowCleverSync: () => boolean;
    getLastCleverSyncDate: () => Moment | undefined;
    getCurrentCleverSyncId: () => string | undefined;
  };
}

interface CleverClassSuccessResult {
  success: true;
  classroom_id: string;
  classroom_name: string;
  status: 'created' | 'updated' | 'archived';
  /** Set when mandark created the class unlicensed because the teacher is not yet approved at its school. */
  unlicensed_reason?: 'teacher_pending';
}

interface CleverClassErrorResult {
  success: false;
  message: string;
  classroom_name: string;
}

export type CleverClassResult = CleverClassSuccessResult | CleverClassErrorResult;

/** A class the sync created without a license because the teacher is pending approval at its school. */
export const isPendingUnlicensedResult = (result: CleverClassResult): boolean =>
  result.success && result.unlicensed_reason === 'teacher_pending';

/** Whether a finished sync has anything the teacher should look at before the modal closes. */
export const syncNeedsReview = (syncResults: CleverSyncResults): boolean =>
  !syncResults.success ||
  !syncResults.results.every((r) => r.success) ||
  syncResults.results.some((r) => r.success && r.status === 'archived') ||
  syncResults.results.some(isPendingUnlicensedResult);

interface CleverSyncSuccessResults {
  success: true;
  results: CleverClassResult[];
}

interface CleverSyncErrorResults {
  success: false;
  message: string;
}

export type CleverSyncResults = CleverSyncSuccessResults | CleverSyncErrorResults;

/** Must stay byte-identical to @clever_teacher_not_found_message in mandark's CleverCourseSync worker (#20297). */
export const CLEVER_TEACHER_NOT_FOUND_MESSAGE = 'No matching Clever teacher account';

/** Frontend-only marker for a sync mandark refused because no Albert district is linked to the teacher's Clever district (#19861). */
export const CLEVER_DISTRICT_NOT_LINKED_MESSAGE = 'District not linked for Clever rostering';

/** Prefills the support chat opened from the district-not-linked dialogue. */
export const CLEVER_DISTRICT_NOT_LINKED_CHAT_MESSAGE =
  "Hi! My district isn't set up for Clever rostering yet. Can you help connect my account?";

export const getUserQuery = () => {
  return resource('user_v2')
    .mandarkEndpoint(['users_v2', masqueradeStore.getUserIdByMasqueradeState()])
    .withMeta('user_v2')
    .customQuery({
      meta: {
        context: {
          user: {
            last_clever_sync_date: true,
            allow_clever_sync: true,
            current_clever_sync_id: true
          }
        }
      }
    });
};
